import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Cashpurr } from '@/components/ui/cashpurr';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { openAIService } from '@/services/openaiService';

import { Send, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChatMessage {
  id: string;
  sender: 'user' | 'cashpurr';
  content: string;
  timestamp: string;
}

interface CashpurrChatProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CashpurrChat: React.FC<CashpurrChatProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userContext, setUserContext] = useState<any>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [prefill, setPrefill] = useState<string | null>(null);
  const [meta, setMeta] = useState<any>(null);

  useEffect(() => {
    if (isOpen && user) {
      loadChatHistory().then(() => {
        // Ensure scroll to bottom after messages are loaded
        setTimeout(scrollToBottom, 200);
      });
      loadUserContext();
      // If there is a prefill message queued, push it once chat opens
      if (prefill) {
        setInputMessage(prefill);
        setPrefill(null);
      }
    }
  }, [isOpen, user]);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  // Scroll to bottom when messages change or chat opens
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, isOpen]);

  useEffect(() => {
    // Listen for custom events to open chat with prefilled message or auto-generated message
    const handleOpenCashpurr = (event: CustomEvent) => {
      if (event.detail?.prefill) {
        setInputMessage(event.detail.prefill);
        // Auto-scroll after prefill
        setTimeout(scrollToBottom, 100);
      } else if (event.detail?.autoMessage) {
        // For auto-generated messages, just reload chat history to show the new message
        if (user) {
          loadChatHistory().then(() => {
            setTimeout(scrollToBottom, 200);
          });
        }
      }
    };

    window.addEventListener('open-cashpurr', handleOpenCashpurr as EventListener);
    return () => {
      window.removeEventListener('open-cashpurr', handleOpenCashpurr as EventListener);
    };
  }, [user]);

  const loadChatHistory = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('cashpurr_chats')
        .select('*')
        .eq('user_id', user.id)
        .order('timestamp', { ascending: true })
        .limit(50);

      if (error) throw error;

      setMessages((data || []).map(msg => ({
        id: msg.id,
        sender: msg.sender as 'user' | 'cashpurr',
        content: msg.content,
        timestamp: msg.timestamp
      })));
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const loadUserContext = async () => {
    if (!user) return;

    try {
      // Load user profile and recent tasks
      const [profileResult, tasksResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('idea_description, development_stage')
          .eq('user_id', user.id)
          .single(),
        supabase
          .from('tasks')
          .select('title, completed')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(5)
      ]);

      const context = {
        idea: profileResult.data?.idea_description,
        developmentStage: profileResult.data?.development_stage,
        recentTasks: tasksResult.data?.map(task => `${task.title} (${task.completed ? 'completed' : 'pending'})`)
      };

      setUserContext(context);
    } catch (error) {
      console.error('Error loading user context:', error);
    }
  };

  const sendMessage = async () => {
    if (!user || !inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    // Add user message to chat
    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMsg]);

    try {
      // Save user message to database
      await supabase
        .from('cashpurr_chats')
        .insert({
          user_id: user.id,
          sender: 'user',
          content: userMessage,
          timestamp: new Date().toISOString(),
          context: userContext ? {
            idea: userContext.idea || null,
            developmentStage: userContext.developmentStage || null,
            recentTasks: userContext.recentTasks || null,
          } : null,
          idea_validation_id: meta?.idea_validation_id || null,
        });

      // Prepare conversation history for AI
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' as const : 'assistant' as const,
        content: msg.content
      }));

      // Add current user message
      conversationHistory.push({
        role: 'user',
        content: userMessage
      });

      // Get AI response
      const aiResponse = await openAIService.createChatCompletion(
        conversationHistory,
        {
          temperature: 0.7,
          max_tokens: 500
        }
      );

      // Add AI response to chat
      const aiMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'cashpurr',
        content: aiResponse,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMsg]);

      // Save AI response to database
      await supabase
        .from('cashpurr_chats')
        .insert({
          user_id: user.id,
          sender: 'cashpurr',
          content: aiResponse,
          timestamp: new Date().toISOString(),
          context: userContext ? {
            idea: userContext.idea || null,
            developmentStage: userContext.developmentStage || null,
            recentTasks: userContext.recentTasks || null,
          } : null,
          idea_validation_id: meta?.idea_validation_id || null,
        });

    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message
      const errorMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'cashpurr',
        content: 'Meow, something went wrong! Please try again. 😿',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  // Allow pages to pre-populate the chat box with suggested follow-ups
  (window as any).cashpurrChat = {
    prefill: (text: string) => {
      if (isOpen) {
        setInputMessage(text);
      } else {
        setPrefill(text);
      }
    },
    setMeta: (m: any) => setMeta(m),
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getCashpurrMood = (content: string): 'happy' | 'critical' | 'thinking' | 'excited' => {
    const lowerContent = content.toLowerCase();
    if (lowerContent.includes('great') || lowerContent.includes('excellent') || lowerContent.includes('🎉')) {
      return 'excited';
    }
    if (lowerContent.includes('need') || lowerContent.includes('should') || lowerContent.includes('must')) {
      return 'critical';
    }
    if (lowerContent.includes('think') || lowerContent.includes('consider') || lowerContent.includes('🤔')) {
      return 'thinking';
    }
    return 'happy';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] h-[600px] flex flex-col p-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Cashpurr size="md" mood="excited" />
              <div>
                <DialogTitle className="text-xl font-bold">Chat with Cashpurr</DialogTitle>
                <p className="text-sm text-muted-foreground">Your investor boss is here to help! 🐱</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
          <div className="space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Cashpurr size="lg" mood="thinking" className="mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Meow! I'm here to help you succeed in your 30-day sprint. 
                  What's on your mind? 😸
                </p>
              </div>
            )}
            
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex items-start space-x-3',
                  message.sender === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {message.sender === 'cashpurr' && (
                  <Cashpurr 
                    size="sm" 
                    mood={getCashpurrMood(message.content)} 
                    className="flex-shrink-0 mt-1"
                  />
                )}
                
                <div
                  className={cn(
                    'max-w-[80%] rounded-lg px-4 py-2',
                    message.sender === 'user'
                      ? 'bg-primary text-primary-foreground ml-auto'
                      : 'bg-muted'
                  )}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {new Date(message.timestamp).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>

                {message.sender === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <span className="text-xs text-primary-foreground font-medium">
                      {user?.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex items-start space-x-3">
                <Cashpurr size="sm" mood="thinking" className="flex-shrink-0 mt-1" />
                <div className="bg-muted rounded-lg px-4 py-2">
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Cashpurr is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        <div className="p-4 border-t">
          <div className="flex space-x-2">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={isLoading}
              className="flex-1"
            />
            <Button 
              onClick={sendMessage} 
              disabled={!inputMessage.trim() || isLoading}
              size="sm"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
